/**
 * Domain tab of the site detail page. Two stacked sections:
 *
 * 1. Site address — the {slug}.aeroapp.ai subdomain, gated at Basic. Renaming
 *    only updates the generatedUrl FIELD on the url doc and never the doc id.
 *    That is safe because nothing resolves a site by doc id == slug:
 *    api/site.js and og.js look sites up with a collectionGroup('url') query on
 *    generatedUrl, api/plausible.js reads the doc by id and uses the
 *    generatedUrl field, and the app routes by doc id. This is the same
 *    never-change-the-id rule FormCustomDomain applies to figmafolio HTML sites.
 *    Validation and uniqueness reuse the slug-generation rules exactly:
 *    isValidReadableSlug (format, length, the RESERVED label set that supersets
 *    RESERVED_LABELS in middleware.js / api/site.js, and the deny list) plus the
 *    same collectionGroup uniqueness query generateReadableSlug uses.
 *
 * 2. Custom domain - connect an external domain you already own, gated at Solo.
 *    This reuses Aero's EXISTING serving layer end to end, no new backend:
 *      - api/domains.js attaches the domain to the Vercel project (which issues
 *        the TLS certificate automatically) and, for html sites only,
 *        registers it in the Vercel Edge Config map.
 *      - html: middleware.js reads that Edge Config map (isHtmlCustomDomain)
 *        and rewrites every path on a registered host to /api/site, and
 *        api/site.js resolves the site by the customDomain FIELD on the url
 *        doc (collectionGroup('url') where customDomain == host).
 *      - figma: the host is NOT in the Edge Config map, so middleware passes
 *        it through to the SPA, and DynamicPage2 resolves the doc by the same
 *        customDomain field (no type filter). buildSiteUrl already prefers
 *        customDomain for figma docs.
 *    So the only write this tab makes for serving is the customDomain field;
 *    the Vercel attach + (html) registry happen through /api/domains. The tab
 *    renders for BOTH site types; figma sites (path-served at
 *    aeroapp.ai/{slug}, no subdomain) get the custom domain card only.
 */
import React, { useState } from 'react';
import firebase, { auth } from '../../../firebase';
import AeroFeatureGate from '../../../components/Aero/AeroFeatureGate';
import { isValidReadableSlug } from '../sitesUtils';

// Same fully-qualified-hostname rule api/domains.js validates against, so the
// client rejects obviously-bad input before the round trip and matches what the
// backend will accept.
const HOSTNAME_REGEX =
  /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

function SiteDomainTab({ user, ownerUid, site, subscriptionType, openUpgradeModal, fetchSite }) {
  const db = firebase.firestore();
  // Shared sites write to the OWNER's path, not the logged-in admin's.
  const writeOwnerUid = ownerUid || user.uid;
  // Figma sites serve path-based at aeroapp.ai/{slug} and have no subdomain
  // address, so they get the custom domain card only.
  const isFigmaSite = site.type === 'figma';
  const [slug, setSlug] = useState(site.generatedUrl || '');
  const [saving, setSaving] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [slugSuccess, setSlugSuccess] = useState('');

  // --- Custom domain ---
  const [domain, setDomain] = useState(site.customDomain || '');
  const [savingDomain, setSavingDomain] = useState(false);
  const [domainError, setDomainError] = useState('');
  const [domainSuccess, setDomainSuccess] = useState('');

  // Light input sanitising: lowercase, spaces to hyphens, drop anything
  // outside [a-z0-9-]. Full validation happens on save (same approach as
  // FormCustomDomain's site-address field).
  const handleSlugChange = (event) => {
    const sanitised = event.target.value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    setSlug(sanitised);
    setSlugError('');
    setSlugSuccess('');
  };

  const handleSave = async () => {
    if (saving) return;
    const newSlug = slug;
    setSlugError('');
    setSlugSuccess('');
    if (newSlug === site.generatedUrl) return;
    if (!isValidReadableSlug(newSlug)) {
      setSlugError('Please choose a valid address: lowercase letters, numbers and single hyphens only.');
      return;
    }
    setSaving(true);
    try {
      // Global uniqueness across every url doc, the same check slug
      // generation uses.
      const snapshot = await db
        .collectionGroup('url')
        .where('generatedUrl', '==', newSlug)
        .limit(1)
        .get();
      if (!snapshot.empty) {
        setSlugError('That address is taken.');
        return;
      }
      // Update only the generatedUrl field; never change the document id.
      await db
        .collection('user').doc(writeOwnerUid)
        .collection('url').doc(site.id)
        .set({ generatedUrl: newSlug, updatedAt: new Date() }, { merge: true });
      await fetchSite();
      setSlugSuccess(`Saved. Your site now lives at ${newSlug}.aeroapp.ai`);
    } catch (err) {
      console.error('Error renaming site address:', err);
      setSlugError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Normalise typed input: drop a protocol, any path, surrounding whitespace
  // and a leading www. — exactly the normalisation api/domains.js and the
  // serving layer apply, so what the user sees is what actually resolves.
  const handleDomainChange = (event) => {
    const value = event.target.value
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/^www\./, '');
    setDomain(value);
    setDomainError('');
    setDomainSuccess('');
  };

  // Add/remove a domain on the Vercel project via the shared /api/domains
  // endpoint. Throws with the API's own error message on a non-2xx so the
  // caller can surface it (e.g. "domain already in use"). ownerUid names the
  // site owner's subtree so the endpoint can authorize a SHARED site
  // (owner-or-sharedWith); for owned sites it equals the caller's uid and the
  // server behaves exactly as before.
  const callDomains = async (action, domainName, idToken) => {
    const res = await fetch('/api/domains', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ action, domain: domainName, docId: site.id, ownerUid: writeOwnerUid }),
    });
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const body = await res.json();
        if (body && body.error && body.error.message) message = body.error.message;
      } catch (e) {
        // Non-JSON error body — keep the status-code message.
      }
      throw new Error(message);
    }
    return res;
  };

  const handleSaveDomain = async () => {
    if (savingDomain) return;
    const newDomain = domain;
    setDomainError('');
    setDomainSuccess('');
    if (newDomain === (site.customDomain || '')) return;
    if (!HOSTNAME_REGEX.test(newDomain)) {
      setDomainError('Please enter a valid domain, e.g. yourdomain.com');
      return;
    }
    setSavingDomain(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      // Release a previously attached domain before attaching the new one:
      // Vercel rejects a re-attach, and a changed domain must free the old one.
      if (site.customDomain && site.customDomain !== newDomain) {
        await callDomains('remove', site.customDomain, idToken);
      }
      // Attach to the Vercel project (auto-provisions TLS) and, for this html
      // site, register the host in the Edge Config map the middleware reads.
      await callDomains('add', newDomain, idToken);
      // The customDomain FIELD is what api/site.js resolves the site by, so
      // this write is what actually makes the domain serve this site.
      await db
        .collection('user').doc(writeOwnerUid)
        .collection('url').doc(site.id)
        .set({ customDomain: newDomain, updatedAt: new Date() }, { merge: true });
      await fetchSite();
      setDomainSuccess(
        `Saved. Once your DNS records resolve, your site will be live at ${newDomain}.`
      );
    } catch (err) {
      console.error('Error saving custom domain:', err);
      setDomainError(err.message || 'Could not save the custom domain.');
    } finally {
      setSavingDomain(false);
    }
  };

  return (
    <div className="aero-sites-domain">
      {/* Site address (html only): figma sites have no subdomain address. */}
      {!isFigmaSite && (
      <AeroFeatureGate
        requiredTier="basic"
        currentTier={subscriptionType}
        icon="window"
        title="Choose your aeroapp.ai address"
        message="Swap the generated address for a short one people can remember."
        onUpgradeClick={openUpgradeModal}
      >
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Site address</div>
          <div className="aero-sites-field-row">
            <input
              className="aero-input"
              type="text"
              value={slug}
              onChange={handleSlugChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              placeholder="your-site"
              disabled={saving}
            />
            <span className="aero-sites-suffix">.aeroapp.ai</span>
            <button
              type="button"
              className="aero-btn aero-btn-primary"
              onClick={handleSave}
              disabled={saving || !slug || slug === site.generatedUrl}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="aero-sites-field-note">Lowercase letters, numbers and hyphens</p>
          <p className="aero-sites-field-warning">Changing it breaks the old link immediately</p>
          {slugError && <p className="aero-sites-field-error">{slugError}</p>}
          {slugSuccess && <p className="aero-sites-field-success">{slugSuccess}</p>}
        </div>
      </AeroFeatureGate>
      )}

      {/* Custom domain - connect an external domain you already own (gated at
          Solo, mirroring Figmafolio's FormCustomDomain). */}
      <AeroFeatureGate
        requiredTier="solo"
        currentTier={subscriptionType}
        icon="globe"
        title="Use your own domain"
        message="Connect a domain you already own."
        onUpgradeClick={openUpgradeModal}
      >
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Custom domain</div>
          <div className="aero-sites-field-row">
            <input
              className="aero-input"
              type="text"
              value={domain}
              onChange={handleDomainChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveDomain();
              }}
              placeholder="yourdomain.com"
              disabled={savingDomain}
            />
            <button
              type="button"
              className="aero-btn aero-btn-primary"
              onClick={handleSaveDomain}
              disabled={savingDomain || !domain || domain === (site.customDomain || '')}
            >
              {savingDomain ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="aero-sites-field-note">
            Add these DNS records with your domain registrar, then save. Your
            certificate is issued automatically once the records resolve.
          </p>
          <div className="aero-sites-dns">
            <div className="aero-sites-dns-row aero-sites-dns-head">
              <span>Type</span>
              <span>Name</span>
              <span>Value</span>
            </div>
            <div className="aero-sites-dns-row">
              <span>A</span>
              <span>@</span>
              <span>76.76.21.21</span>
            </div>
            <div className="aero-sites-dns-row">
              <span>CNAME</span>
              <span>www</span>
              <span>cname.vercel-dns.com</span>
            </div>
          </div>
          {domainError && <p className="aero-sites-field-error">{domainError}</p>}
          {domainSuccess && <p className="aero-sites-field-success">{domainSuccess}</p>}
        </div>
      </AeroFeatureGate>
    </div>
  );
}

export default SiteDomainTab;
